import { PageShell } from '@/components/app/page-shell';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useActiveOrganization } from '@/features/organization';
import { queryKeys } from '@/lib/query-keys';
import { useSession } from '@/lib/session';
import { apiClient } from '@borradh-workspace/api-client';
import { organizationDeletionSummarySchema } from '@borradh-workspace/contracts';
import { useQuery } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { DeletionRequestedScreen } from './deletion-requested-screen';
import {
  DELETION_INTENT_KEY as INTENT_KEY,
  useRequestWorkspaceDeletion,
} from './use-workspace-deletion';
const PAGE = '/dashboard/settings/delete-account';
const requiresFreshSignIn = (message: string) =>
  /sign in (?:as .*? )?again|fresh sign-in/i.test(message);
export function DeleteWorkspacePage({
  confirm = false,
}: { confirm?: boolean }) {
  const { data: organization } = useActiveOrganization();
  const { data: session } = useSession();
  const owner =
    session?.user?.orgRole === 'owner' &&
    session.user.organizationId === organization?.id;
  const summary = useQuery({
    queryKey: queryKeys.privacy.organizationDeletionSummary(
      organization?.id,
      session?.user?.id
    ),
    enabled: !!organization?.id && owner,
    retry: false,
    staleTime: 0,
    queryFn: () =>
      apiClient.get(`organizations/${organization?.id}/deletion-summary`, {
        schema: organizationDeletionSummarySchema,
      }),
  });
  const [confirmation, setConfirmation] = useState('');
  const [open, setOpen] = useState(confirm);
  const [authError, setAuthError] = useState('');
  const needsSignIn = /sign in (?:as .*? )?again|fresh sign-in/i.test(
    authError
  );
  const validIntent = () => {
    const intentCheckedAt = Date.now();
    try {
      const intent = JSON.parse(sessionStorage.getItem(INTENT_KEY) ?? 'null');
      return (
        intent?.organizationId === organization?.id &&
        intent?.userId === session?.user?.id &&
        intentCheckedAt - intent.startedAt < 10 * 60 * 1000 &&
        intent.startedAt <= intentCheckedAt
      );
    } catch {
      return false;
    }
  };
  const authenticate = () => {
    if (!owner || !summary.data) return;
    sessionStorage.setItem(
      INTENT_KEY,
      JSON.stringify({
        organizationId: organization?.id,
        userId: session?.user?.id,
        startedAt: Date.now(),
      })
    );
    window.location.assign(
      `/sign-in?reason=workspace-deletion&redirect=${encodeURIComponent(`${PAGE}?confirm=true`)}`
    );
  };
  const request = useRequestWorkspaceDeletion({
    organizationId: organization?.id,
    expectedName: confirmation.trim(),
    validIntent,
    onError: (error: Error) => {
      setAuthError(error.message || 'Unable to delete the workspace');
      if (!requiresFreshSignIn(error.message))
        toast.error(error.message || 'Unable to delete the workspace');
    },
  });
  // Before the owner checks: the closed clinic's session data may refetch.
  if (request.isSuccess) return <DeletionRequestedScreen />;
  if (!owner)
    return (
      <PageShell>
        <h1 className="text-2xl font-semibold">Delete account</h1>
        <p className="text-sm text-muted-foreground">
          Only the clinic owner can delete this workspace.
        </p>
      </PageShell>
    );
  if (!summary.data)
    return (
      <PageShell>
        <h1 className="text-2xl font-semibold">Delete account</h1>
        {summary.isError ? (
          <div role="alert" className="space-y-3">
            <p>
              We could not load the clinic’s data counts. No deletion has been
              requested.
            </p>
            <Button variant="outline" onClick={() => summary.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <p role="status">Loading the clinic’s data counts…</p>
        )}
      </PageShell>
    );
  if (!summary.data.deletionAvailable)
    return (
      <PageShell>
        <h1 className="text-2xl font-semibold">Delete account</h1>
        <p role="status">
          Workspace deletion is not available yet. No deletion has been
          requested.
        </p>
      </PageShell>
    );
  const clinic = summary.data;
  return (
    <PageShell>
      <h1 className="text-2xl font-semibold">Delete account</h1>
      <div className="max-w-3xl space-y-6 text-sm leading-6">
        <p>
          In accordance with Article 17 of the European Union General Data
          Protection Regulation (EU GDPR), applicable since 25 May 2018, and the
          UK GDPR and Data Protection Act 2018, as amended by the Data (Use and
          Access) Act 2025, individuals may exercise their “right to be
          forgotten”, subject to legal exceptions.
        </p>
        <p>
          Borradh Technologies Limited, trading as Borrah and Borrah.com, is
          referred to below as “we”. As the clinic owner, you are instructing us
          to close <strong>{clinic.name}</strong> and erase its workspace data.
          Closing a clinic workspace is separate from an individual patient’s
          right to erasure.
        </p>
        <section className="space-y-3">
          <h2 className="text-base font-semibold">
            The data covered by your request
          </h2>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>{clinic.customers.toLocaleString()} customers</strong>,
              including their contact details, notes, consent records and other
              data held in this workspace.
            </li>
            <li>
              {clinic.conversations.toLocaleString()} conversations and their
              messages and attachments.
            </li>
            <li>
              {clinic.appointments.toLocaleString()} appointments and associated
              booking records.
            </li>
            <li>
              {clinic.documents.toLocaleString()} patient documents and uploaded
              files. Clinical forms and related records are also covered by this
              request.
            </li>
            <li>
              Workspace access and invitations for{' '}
              {clinic.staff.toLocaleString()} staff accounts. Personal sign-in
              accounts are separate from the clinic workspace.
            </li>
            <li>
              Workspace settings, services, campaigns, generated content, media,
              reports and connected service credentials.
            </li>
          </ul>
          <p className="text-muted-foreground">
            Counts include records already marked for deletion. They may change
            before you confirm. Data kept outside Borrah, including copies you
            downloaded or services you manage, is outside this request.
          </p>
        </section>
        <section className="space-y-3">
          <h2 className="text-base font-semibold">
            What happens when you confirm
          </h2>
          <p>
            The clinic workspace will be soft deleted and access will end for
            everyone. Permanent cleanup is scheduled to start 30 days after
            confirmation, including requests to our service providers. You must
            export records you need before you continue.
          </p>
          <p>
            Do not rely on the 30-day period as a backup or a promise that we
            can restore your data. While recovery remains available, the owner
            can sign in and open the organization recovery page. Once permanent
            erasure starts, deletion cannot be undone. We will not retain data
            longer than needed, except where the law requires it.
          </p>
        </section>
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Your subscription</h2>
          <p>
            Workspace closure does not cancel a paid subscription at once.
            Cancel it in Billing before continuing to avoid further charges.
          </p>
          <a
            className="underline underline-offset-4"
            href="/dashboard/settings/billing"
          >
            Open Billing
          </a>
        </section>
        <section className="space-y-3">
          <h2 className="text-base font-semibold">
            Records that may need to be kept
          </h2>
          <p>
            Erasure rights are not absolute. Records needed to meet legal
            duties, retain clinical records, prevent fraud, or establish,
            exercise or defend legal claims may need to be retained with
            restricted access. Limited billing, security and erasure audit
            records may also need to be kept for those purposes.
          </p>
          <p>
            As the clinic’s data controller, you must meet your own clinical,
            legal and record retention duties before instructing us to erase
            patient data. This page does not waive any patient’s rights or
            remove those duties.
          </p>
          <p>
            Backups and third-party copies may follow separate deletion
            schedules. Data awaiting backup expiry must remain beyond normal
            use, and deletion must be reapplied if a backup is restored. We will
            explain any lawful exception or delay that affects your request.
          </p>
        </section>
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Your rights</h2>
          <p>
            We handle valid individual erasure requests without undue delay and
            normally respond within one calendar month. The 30-day workspace
            closure schedule does not replace that duty. If you cannot sign in
            to request deletion, contact us through the privacy notice. You can
            also raise a complaint with your data protection authority, or seek
            a court remedy. Nothing on this page limits those rights.
          </p>
          <a
            className="underline underline-offset-4"
            href="https://borrah.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Read the privacy notice
          </a>
        </section>
        {authError && !open && (
          <p role="alert" className="text-destructive">
            {authError}
          </p>
        )}
        <div className="border-t pt-6 space-y-3">
          <p className="font-medium">
            The next step asks you to sign in again, including two-factor
            authentication if enabled. You will then see one final confirmation.
          </p>
          <Button
            size="lg"
            variant="destructive"
            onClick={authenticate}
            disabled={request.isPending}
          >
            Delete account and clinic data
          </Button>
        </div>
        <Dialog
          open={open && validIntent()}
          onOpenChange={(value) => {
            if (!request.isPending) setOpen(value);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {clinic.name.trim()}?</DialogTitle>
              <DialogDescription>
                Everyone will lose access now. Permanent cleanup starts after 30
                days. Export any records you need before confirming.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="delete-clinic-name">
                Type the clinic name to confirm
              </Label>
              <Input
                id="delete-clinic-name"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="off"
                disabled={request.isPending}
              />
            </div>
            {authError && (
              <Alert
                variant={needsSignIn ? 'default' : 'destructive'}
                className={
                  needsSignIn
                    ? 'border-amber-200 bg-amber-50/60 [&>svg]:text-amber-700'
                    : undefined
                }
              >
                <KeyRound aria-hidden="true" />
                <AlertTitle>
                  {needsSignIn
                    ? 'Sign in again to continue'
                    : 'We could not delete the workspace'}
                </AlertTitle>
                <AlertDescription>
                  {needsSignIn
                    ? 'Your sign-in could not be verified. No data has been deleted. Sign in again, then confirm your request.'
                    : authError}
                </AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button
                autoFocus
                variant="outline"
                disabled={request.isPending}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              {needsSignIn ? (
                <Button onClick={authenticate}>Sign in again</Button>
              ) : (
                <Button
                  variant="destructive"
                  disabled={
                    request.isPending ||
                    !confirmation.trim() ||
                    confirmation.trim() !== clinic.name.trim()
                  }
                  onClick={() => request.mutate()}
                >
                  {request.isPending ? 'Deleting…' : 'Confirm deletion'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageShell>
  );
}
